import React from 'react';
import { playAudioStream, playAudioBuffer } from '../utils/audio';
import { trackEvent, useReadCloudAuth } from '@readcloud/common';
import { tauriFetch } from '@readcloud/fetch-wrapper';
import { ABSOLUTE_PATH } from '@readcloud/state';

let nextEventId = 1;
export const useTTS = (props: { disabled?: boolean; text?: string }) => {
  const [playingAudio, setPlayingAudio] = React.useState<
    ReturnType<typeof playAudioStream> | undefined
  >();
  const { user } = useReadCloudAuth();

  const mounted = React.useRef(true);

  React.useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const handleListen = React.useMemo(() => {
    const disableTTS = props.disabled;
    const selectedText = props.text;
    if (disableTTS || !selectedText) {
      return undefined;
    } else {
      return async () => {
        trackEvent('Webapp/TextToSpeachUsed', {});
        if (!props.text) return;

        // Create AudioContext synchronously during the user gesture (click).
        // iOS requires AudioContext creation/resume within a user-initiated event;
        // awaiting the fetch below would break the gesture chain.
        const context = new AudioContext();
        if (context.state === 'suspended') {
          await context.resume();
        }

        try {
          const res = await tauriFetch(
            `${ABSOLUTE_PATH}/api/v14/tts?text=${encodeURIComponent(selectedText)}&voiceId=Joanna`,
            {
              headers: {
                Accept: 'application/octet-stream',
                Authorization: `Bearer ${user?.accessToken}`,
              },
              method: 'GET',
            }
          );
          if (!res.ok) {
            context.close();
            throw new Error('TTS fetch failed');
          }
          if (!mounted.current) {
            context.close();
            return;
          }

          const eventId = nextEventId++;
          const onDone = (id: string | number) => {
            context.close();
            setPlayingAudio((state) =>
              state?.eventId === id ? undefined : state
            );
          };

          // Use streaming playback if ReadableStream body is available,
          // otherwise fall back to full buffer playback (required on iOS/mobile
          // where Tauri's HTTP plugin may not support streaming responses).
          if (res.body) {
            setPlayingAudio(playAudioStream(eventId, res.body, onDone, context));
          } else {
            const arrayBuffer = await res.arrayBuffer();
            if (!mounted.current) {
              context.close();
              return;
            }
            setPlayingAudio(playAudioBuffer(eventId, arrayBuffer, onDone, context));
          }
        } catch (error) {
          context.close();
          console.error(error);
        }
      };
    }
  }, [props.text, user?.accessToken]);

  const handleStopAudio = React.useCallback(() => {
    setPlayingAudio((audio) => {
      if (audio) {
        audio.cancel();
      }
      return undefined;
    });
  }, []);

  React.useEffect(() => {
    if (playingAudio && !props.disabled) {
      handleStopAudio();
    }
  }, [props.text]);

  // Stop any audio before it changes (this handles the unmounting case)
  React.useEffect(() => {
    if (playingAudio) {
      return () => {
        playingAudio.cancel();
      };
    } else {
      return undefined;
    }
  }, [playingAudio]);

  return {
    handleStopAudio,
    handleListen,
    playingAudio,
  };
};
